import {
  Component,
  OnInit,
  ChangeDetectorRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { Router } from '@angular/router';
import { UserService } from '../../../services/user/user.service';

import { LocationDTO } from '../../../dto/location/location.dto';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { MatIconModule } from '@angular/material/icon';
import { environment } from '../../../environment/environment';
import { catchError } from 'rxjs/operators';
import { EMPTY } from 'rxjs';
import { DunaProfileImageComponent } from '../duna-profile-image/duna-profile-image.component';

@Component({
  selector: 'app-duna-mine-stationer',
  standalone: true,
  imports: [
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    DunaProfileImageComponent,
  ],
  templateUrl: './duna-mine-stationer.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './duna-mine-stationer.component.scss',
})
export class DunaMineStationerComponent implements OnInit {
  locations: LocationDTO[] = [];
  error: string | null = null;
  loaded = false;
  private viewedUserUuid: string | null = null;
  private primaryLocationNames = new Set<string>();
  private secondaryLocationNames = new Set<string>();

  constructor(
    public router: Router,
    private userService: UserService,
    private cdr: ChangeDetectorRef,
    private http: HttpClient
  ) {}

  ngOnInit() {
    const currentUrl = window.location.pathname;
    const profileMatch = currentUrl.match(/\/profile\/([a-f0-9-]{36})/);

    if (profileMatch) {
      this.viewedUserUuid = profileMatch[1];
    }

    this.loadUserAssignments();
    this.loadLocations();
  }

  private loadUserAssignments() {
    this.userService.getCurrentUser().subscribe({
      next: (user) => {
        this.primaryLocationNames = this.toNormalizedNameSet(
          user?.roles?.['primaer']
        );
        this.secondaryLocationNames = this.toNormalizedNameSet(
          user?.roles?.['sekundaer']
        );
        this.cdr.detectChanges();
      },
      error: () => {
        this.primaryLocationNames = new Set<string>();
        this.secondaryLocationNames = new Set<string>();
      },
    });
  }

  private toNormalizedNameSet(values: unknown): Set<string> {
    const names = new Set<string>();
    if (!Array.isArray(values)) {
      return names;
    }

    for (const value of values) {
      if (typeof value === 'string' && value.trim()) {
        names.add(value.trim().toLowerCase());
        continue;
      }

      if (value && typeof value === 'object') {
        const record = value as Record<string, unknown>;
        const nested = [
          record['name'],
          record['clean_name'],
          record['locations_name'],
        ];
        for (const candidate of nested) {
          if (typeof candidate === 'string' && candidate.trim()) {
            names.add(candidate.trim().toLowerCase());
          }
        }
      }
    }

    return names;
  }

  private loadLocations() {
    // If viewing another user's profile, fetch their locations
    if (this.viewedUserUuid) {
      this.http
        .get<LocationDTO[]>(
          `${environment.apiUrl}/users/${this.viewedUserUuid}/locations`
        )
        .pipe(
          catchError((error: HttpErrorResponse) => {
            console.error(
              '[DunaMineStationerComponent] Error fetching user locations:',
              error
            );
            this.error = 'Kunne ikke indlæse lokationer';
            this.locations = [];
            this.loaded = true;
            this.cdr.detectChanges();
            return EMPTY;
          })
        )
        .subscribe({
          next: (locations) => this.setLocations(locations),
        });
    } else {
      this.userService.getUserLocationsFromApi().subscribe({
        next: (locations) => this.setLocations(locations),
        error: (error: HttpErrorResponse) => {
          console.error(
            '[DunaMineStationerComponent] Error loading locations:',
            error
          );
          this.locations = [];
          this.error = 'Kunne ikke indlæse lokationer';
          this.loaded = true;
          this.cdr.detectChanges();
        },
      });
    }
  }

  get emptyStateTitle(): string {
    return 'Ingen lokationer tilknyttet';
  }

  get emptyStateMessage(): string {
    return this.viewedUserUuid
      ? 'Brugeren er ikke tilknyttet nogen lokationer.'
      : 'Du er ikke tilknyttet nogen lokationer.';
  }

  private setLocations(locations: LocationDTO[] | null | undefined): void {
    this.locations = [...(locations ?? [])];
    this.error = null;
    this.loaded = true;
    this.cdr.detectChanges();
  }

  getLocationAssignment(
    location: LocationDTO
  ): { type: 'primaer' | 'sekundaer'; label: string } | null {
    const candidates = [location.clean_name, location.locations_name]
      .filter((name): name is string => !!name?.trim())
      .map((name) => name.trim().toLowerCase());

    if (candidates.some((name) => this.primaryLocationNames.has(name))) {
      return { type: 'primaer', label: 'Primær' };
    }
    if (candidates.some((name) => this.secondaryLocationNames.has(name))) {
      return { type: 'sekundaer', label: 'Sekundær' };
    }
    return null;
  }

  goToLocation(location: LocationDTO): void {
    this.router.navigate(['/lokation', location.guid]);
  }

  getLocationType(location: LocationDTO): string {
    return location.data?.type || 'Brandstation';
  }

  getLocationAddress(location: LocationDTO): string {
    return location.data?.locations_address || '';
  }

  getLocationInitials(location: LocationDTO): string {
    const name = location.locations_name ?? '';
    return name ? name.charAt(0).toUpperCase() : '?';
  }

  getLocationProfileImage(location: LocationDTO): string | null {
    return (
      location.profile_image_obfuscated_name ??
      location.data?.profile_image_obfuscated_name ??
      null
    );
  }
}
